import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Eye, EyeOff, ArrowRight, CheckCircle } from "lucide-react";
import axios from "axios";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPECIALTIES = [
  "General Practice",
  "Pediatrics",
  "Cardiology",
  "Dermatology",
  "Orthopedics",
  "Neurology",
  "Gynecology",
  "Psychiatry",
  "Dentistry",
  "Other",
];

const FEATURES = [
  {
    title: "24/7 Voice AI",
    desc: "Never miss a patient call — your AI receptionist answers around the clock.",
  },
  {
    title: "Smart Scheduling",
    desc: "Patients book appointments automatically via voice or web.",
  },
  {
    title: "WhatsApp Reminders",
    desc: "Automated reminders reduce no-shows and keep patients informed.",
  },
];

export default function Signup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    owner_name: "",
    clinic_name: "",
    email: "",
    specialty: "",
    password: "",
    confirmPassword: "",
  });
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (field) => (e) =>
    setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/signup`, {
        email: form.email,
        password: form.password,
        owner_name: form.owner_name,
        clinic_name: form.clinic_name,
        specialty: form.specialty,
      });

      localStorage.setItem("clinic_admin_token", data.access_token);
      localStorage.setItem("clinic_admin_user", data.email ?? form.email);
      localStorage.setItem("clinic_id", data.clinic_id ?? "");
      localStorage.setItem("clinic_name", data.clinic_name ?? form.clinic_name);
      localStorage.setItem("onboarding_completed", "false");

      toast.success("Account created! Let's set up your clinic.");
      navigate("/onboarding");
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409) {
        setError("An account with this email already exists.");
      } else {
        setError(detail || "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0D14] flex overflow-hidden relative">
      {/* Global ambient gradients */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full bg-cyan-500/5 blur-3xl"
          animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -bottom-40 -right-40 w-[700px] h-[700px] rounded-full bg-violet-500/5 blur-3xl"
          animate={{ scale: [1.1, 1, 1.1], opacity: [0.5, 0.9, 0.5] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
        />
        <motion.div
          className="absolute top-1/2 left-1/3 w-[400px] h-[400px] rounded-full bg-cyan-400/3 blur-3xl"
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 3 }}
        />
      </div>

      {/* ── Left branding panel ── */}
      <div className="hidden lg:flex lg:w-[45%] flex-col justify-between p-12 relative border-r border-white/5">
        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.3) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(255,255,255,.3) 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />

        <div className="relative">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-16">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-violet-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <Phone className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold font-outfit text-white text-xl tracking-tight">
              Careline
            </span>
          </div>

          {/* Headline */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl xl:text-5xl font-bold font-outfit text-white leading-tight">
              The smarter way to{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-violet-400 bg-clip-text text-transparent">
                run your clinic
              </span>
            </h1>
            <p className="text-slate-400 mt-4 text-lg leading-relaxed">
              Join{" "}
              <span className="text-cyan-400 font-semibold">500+ clinics</span>{" "}
              already using Careline AI to automate patient communication.
            </p>
          </motion.div>

          {/* Feature bullets */}
          <div className="mt-10 space-y-5">
            {FEATURES.map(({ title, desc }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 + i * 0.12 }}
                className="flex items-start gap-3"
              >
                <div className="mt-0.5 w-6 h-6 rounded-full bg-gradient-to-br from-cyan-500/20 to-violet-500/20 border border-cyan-500/30 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-3.5 h-3.5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">{title}</p>
                  <p className="text-slate-500 text-xs mt-0.5 leading-relaxed">{desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="flex items-center gap-2 text-slate-600 text-xs">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            System operational · All services running
          </div>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 relative overflow-y-auto">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2.5 mb-10">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-violet-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
            <Phone className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold font-outfit text-white text-lg">Careline</span>
        </div>

        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-7">
            <h2 className="text-2xl font-bold font-outfit text-white">Create your account</h2>
            <p className="text-slate-400 text-sm mt-1">
              Set up your clinic in under 2 minutes
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-[#161B27] border border-white/10 rounded-2xl p-8 space-y-4 shadow-2xl shadow-black/40"
          >
            {/* Full Name */}
            <div>
              <label className="text-xs font-medium text-slate-400 block mb-1.5 uppercase tracking-wider">
                Full Name
              </label>
              <input
                type="text"
                value={form.owner_name}
                onChange={set("owner_name")}
                placeholder="Dr. Jane Smith"
                required
                className="bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 focus:outline-none rounded-lg px-3 py-2 text-sm w-full transition-all"
              />
            </div>

            {/* Clinic Name */}
            <div>
              <label className="text-xs font-medium text-slate-400 block mb-1.5 uppercase tracking-wider">
                Clinic Name
              </label>
              <input
                type="text"
                value={form.clinic_name}
                onChange={set("clinic_name")}
                placeholder="Sunrise Medical Center"
                required
                className="bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 focus:outline-none rounded-lg px-3 py-2 text-sm w-full transition-all"
              />
            </div>

            {/* Email */}
            <div>
              <label className="text-xs font-medium text-slate-400 block mb-1.5 uppercase tracking-wider">
                Email Address
              </label>
              <input
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="jane@clinic.com"
                required
                className="bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 focus:outline-none rounded-lg px-3 py-2 text-sm w-full transition-all"
              />
            </div>

            {/* Specialty */}
            <div>
              <label className="text-xs font-medium text-slate-400 block mb-1.5 uppercase tracking-wider">
                Specialty
              </label>
              <select
                value={form.specialty}
                onChange={set("specialty")}
                required
                className="bg-[#0A0D14] border border-white/10 text-white focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 focus:outline-none rounded-lg px-3 py-2 text-sm w-full transition-all appearance-none cursor-pointer"
              >
                <option value="" disabled className="text-slate-600">
                  Select your specialty
                </option>
                {SPECIALTIES.map((s) => (
                  <option key={s} value={s} className="bg-[#0A0D14] text-white">
                    {s}
                  </option>
                ))}
              </select>
            </div>

            {/* Password */}
            <div>
              <label className="text-xs font-medium text-slate-400 block mb-1.5 uppercase tracking-wider">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  value={form.password}
                  onChange={set("password")}
                  placeholder="••••••••"
                  required
                  className="bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 focus:outline-none rounded-lg px-3 py-2 pr-10 text-sm w-full transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="text-xs font-medium text-slate-400 block mb-1.5 uppercase tracking-wider">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirm ? "text" : "password"}
                  value={form.confirmPassword}
                  onChange={set("confirmPassword")}
                  placeholder="••••••••"
                  required
                  className="bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 focus:outline-none rounded-lg px-3 py-2 pr-10 text-sm w-full transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Inline error */}
            {error && (
              <motion.p
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2"
              >
                {error}
              </motion.p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-violet-600 text-white font-semibold py-2.5 rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity shadow-lg shadow-cyan-500/20 mt-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Create Account
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-5">
            Already have an account?{" "}
            <Link
              to="/admin/login"
              className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
            >
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
